import { Controller } from "@hotwired/stimulus"

// Lets the user hide/show table columns. Every th/td of a column carries `data-column="<key>"`;
// each `toggle` checkbox carries `data-column-key="<key>"`. The hidden keys are remembered in
// localStorage (under `storageKey`) so the choice survives reloads.
export default class extends Controller {
  static values = { storageKey: String }
  static targets = ["toggle"]
  declare readonly storageKeyValue: string
  declare readonly toggleTargets: HTMLInputElement[]

  connect() {
    const hidden = this.readHidden()
    this.toggleTargets.forEach((box) => {
      box.checked = !hidden.includes(box.dataset.columnKey ?? "")
    })
    this.toggleTargets.forEach((box) => this.apply(box))
  }

  // Checkbox `change`: show/hide that column and persist.
  toggle(event: Event) {
    this.apply(event.target as HTMLInputElement)
    this.writeHidden(
      this.toggleTargets
        .filter((box) => !box.checked)
        .map((box) => box.dataset.columnKey ?? ""),
    )
  }

  showAll() {
    this.toggleTargets.forEach((box) => {
      box.checked = true
      this.apply(box)
    })
    try {
      localStorage.removeItem(this.storageKeyValue)
    } catch {
      // storage unavailable - nothing to clear
    }
  }

  private apply(box: HTMLInputElement) {
    const key = box.dataset.columnKey
    if (!key) return
    this.element
      .querySelectorAll(`[data-column="${key}"]`)
      .forEach((el) => el.classList.toggle("hidden", !box.checked))
  }

  private readHidden(): string[] {
    try {
      const parsed: unknown = JSON.parse(
        localStorage.getItem(this.storageKeyValue) ?? "[]",
      )
      return Array.isArray(parsed)
        ? parsed.filter((k): k is string => typeof k === "string")
        : []
    } catch {
      return []
    }
  }

  private writeHidden(keys: string[]) {
    try {
      localStorage.setItem(this.storageKeyValue, JSON.stringify(keys))
    } catch {
      // storage unavailable - the choice just won't persist
    }
  }
}
